// src/pages/ChangePasswordPage.jsx
import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import AuthService from "../services/AuthService";
import AuthPageLogo from "../components/AuthPageLogo";
import "bootstrap/dist/css/bootstrap.min.css";

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [show, setShow] = useState({
    current: false,
    next: false,
    confirm: false,
  });
  const [loading, setLoading] = useState(false);

  const validate = () => {
    if (!currentPassword) return "Please enter your current password";
    if (!newPassword || newPassword.length < 8)
      return "New password must be at least 8 characters";
    if (newPassword === currentPassword)
      return "New password must be different from current password";
    if (newPassword !== confirmNewPassword)
      return "New password and confirm password do not match";
    return null;
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) return toast.error(err);

    setLoading(true);
    try {
      const res = await AuthService.changePassword({
        currentPassword,
        newPassword,
        confirmNewPassword,
      });

      // Expecting a success shape like:
      // { success: true, message: "Password changed successfully" }
      if (res?.success) {
        toast.success(res.message || "Password changed successfully.");
        // Optional: log the user out or send back to dashboard
        navigate("/dashboard");
      } else {
        toast.error(res?.message || "Unable to change password.");
      }
    } catch (error) {
      console.error(error);
      const msg =
        error?.response?.data?.message ||
        "Failed to change password. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid">
      <div className="row justify-content-center">
        <div className="col-md-5 col-12">
          <div className="boder-bg-light rounded-3 d-flex align-items-start justify-content-center bg-white p-5 m-5">
            <div className="w-100 p-5">
              <div className="text-center mb-4">
                <AuthPageLogo />
                <h3 className="fw-semibold">Change Password</h3>
                <p className="text-muted mb-0">Keep your account secure</p>
              </div>

              <form onSubmit={handleChangePassword}>
                <div className="mb-3">
                  <label className="form-label" htmlFor="currentPassword">
                    Current Password
                  </label>
                  <div className="input-group">
                    <input
                      id="currentPassword"
                      type={show.current ? "text" : "password"}
                      className="form-control"
                      placeholder="Enter current password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() =>
                        setShow((s) => ({ ...s, current: !s.current }))
                      }
                      aria-label="Toggle current password visibility"
                    >
                      {show.current ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label" htmlFor="newPassword">
                    New Password <span className="text-muted">(min 8 chars)</span>
                  </label>
                  <div className="input-group">
                    <input
                      id="newPassword"
                      type={show.next ? "text" : "password"}
                      className="form-control"
                      placeholder="Enter new password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() =>
                        setShow((s) => ({ ...s, next: !s.next }))
                      }
                      aria-label="Toggle new password visibility"
                    >
                      {show.next ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>

                <div className="mb-2">
                  <label className="form-label" htmlFor="confirmNewPassword">
                    Confirm New Password
                  </label>
                  <div className="input-group">
                    <input
                      id="confirmNewPassword"
                      type={show.confirm ? "text" : "password"}
                      className="form-control"
                      placeholder="Re-enter new password"
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary"
                      onClick={() =>
                        setShow((s) => ({ ...s, confirm: !s.confirm }))
                      }
                      aria-label="Toggle confirm password visibility"
                    >
                      {show.confirm ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>

                <button
                  type="submit"
                  className="btn btn-primary w-100 py-2 fw-semibold mt-3"
                  disabled={loading}
                >
                  {loading ? "Please wait..." : "Update Password"}
                </button>
              </form>

              <p className="text-center text-muted mt-4">
                <Link className="text-primary text-decoration-none">
                  Home
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
